// src/contexts/AuthContext.js - COMPLETE WITH FCM CLEANUP

import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, googleProvider, safeSignInWithPopup } from '../firebase/config';
import { doc, setDoc } from 'firebase/firestore';
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  getRedirectResult,
  onAuthStateChanged
} from 'firebase/auth';
import { isPWA, storePWASession, isPWASessionValid, logPWASession } from '../utils/pwaDetection';
import NotificationService from '../services/NotificationService'; // ✅ ADD THIS
import { resolveShopInvites } from './ActiveShopContext';
import { parseSubdomain } from '../utils/subdomainRouter';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPWAMode, setIsPWAMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ✅ Initialize PWA detection on mount
  useEffect(() => {
    const pwaMode = isPWA();
    setIsPWAMode(pwaMode);
    
    if (pwaMode) {
      logPWASession();
      console.log('📱 Running as installed PWA - Extended session enabled (7 days)');
    }
  }, []);

  // ✅ Auth state listener with PWA session tracking
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth, 
      async (firebaseUser) => {
        console.log('🔐 Auth state changed:', firebaseUser ? 'User logged in' : 'No user');
        
        if (firebaseUser) {
          setUser(firebaseUser);
          setIsAuthenticated(true);

          // Set cross-subdomain session cookie (production only)
          if (window.location.hostname.includes('kalkode.com')) {
            try {
              const idToken = await firebaseUser.getIdToken();
              await fetch('/api/guest-operations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operation: 'session_login', idToken })
              });
            } catch (e) {
              console.warn('⚠️ Cross-subdomain session cookie failed (non-critical):', e);
            }
          }

          // ✅ Store PWA session metadata
          if (isPWAMode) {
            storePWASession();
            localStorage.setItem('pwa_user_id', firebaseUser.uid);
            localStorage.setItem('pwa_login_timestamp', Date.now().toString());
            console.log('✅ PWA session stored - Valid for 7 days');
          }
          
          // ✅ NEW: Cleanup old/invalid tokens on login
          if (!firebaseUser.isAnonymous) {
            // Ensure email is persisted in the user document so invite lookups work
            if (firebaseUser.email) {
              try {
                await setDoc(
                  doc(db, 'users', firebaseUser.uid),
                  { email: firebaseUser.email, uid: firebaseUser.uid },
                  { merge: true }
                );
              } catch (e) {
                console.warn('⚠️ Could not persist email to user doc:', e);
              }
            }

            try {
              await NotificationService.cleanupInvalidTokens(firebaseUser.uid);
              console.log('🧹 Cleaned up old FCM tokens');
            } catch (cleanupError) {
              console.warn('⚠️ Token cleanup failed (non-critical):', cleanupError);
            }

            // Resolve any pending shop invites for this user's email
            try {
              await resolveShopInvites(firebaseUser);
            } catch (inviteError) {
              console.warn('⚠️ Shop invite resolution failed (non-critical):', inviteError);
            }
          }
          
        } else {
          // Try to restore session from cross-subdomain cookie when on a shop subdomain
          const { isSubdomain } = parseSubdomain();
          if (isSubdomain) {
            try {
              const response = await fetch('/api/guest-operations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operation: 'session_verify' })
              });
              if (response.ok) {
                const { customToken } = await response.json();
                await signInWithCustomToken(auth, customToken);
                // onAuthStateChanged will fire again with the restored user
                return;
              }
            } catch (e) {
              console.warn('⚠️ Cross-subdomain session restore failed (non-critical):', e);
            }
          }

          // Check if we should restore session for PWA
          if (isPWAMode && isPWASessionValid()) {
            console.log('⏳ PWA session still valid - Firebase will auto-restore');
          } else {
            setUser(null);
            setIsAuthenticated(false);
            
            // Clean up PWA session data
            if (isPWAMode) {
              localStorage.removeItem('pwa_user_id');
              localStorage.removeItem('pwa_login_timestamp');
              localStorage.removeItem('pwa_last_active');
            }
          }
        }
        
        setLoading(false);
      },
      (authError) => {
        console.error('❌ Auth state change error:', authError);
        setError(authError.message);
        setLoading(false);
      }
    );

    // Handle redirect result
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user && isPWAMode) {
          storePWASession();
          localStorage.setItem('pwa_user_id', result.user.uid);
          localStorage.setItem('pwa_login_timestamp', Date.now().toString());
        }
      })
      .catch(error => {
        console.error('❌ Error getting redirect result:', error);
        setError(error.message);
      });

    return () => unsubscribe();
  }, [isPWAMode]);

  // ✅ Track PWA activity to extend session
  useEffect(() => {
    if (!isPWAMode || !user) return;

    const updateLastActive = () => {
      localStorage.setItem('pwa_last_active', Date.now().toString());
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateLastActive, { passive: true });
    });

    const interval = setInterval(updateLastActive, 5 * 60 * 1000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateLastActive);
      });
      clearInterval(interval);
    };
  }, [isPWAMode, user]);

  const clearError = () => setError(null);

  // ✅ Sign in with Google - with PWA session
  const signInWithGoogle = async () => {
    clearError();
    try {
      const result = await safeSignInWithPopup(googleProvider);

      // ✅ Store PWA session immediately on login
      if (isPWAMode) {
        storePWASession();
        localStorage.setItem('pwa_user_id', result.user.uid);
        localStorage.setItem('pwa_login_timestamp', Date.now().toString());
        console.log('✅ PWA session created on Google login');
      }

      return result.user;
    } catch (error) {
      console.error('❌ Error signing in with Google:', error);
      // Normalize popup-close handling: don't show a global redirect in this case
      if (error && (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/popup-in-progress' || error.code === 'auth/cancelled-popup-request')) {
        // Let callers decide how to handle popup-close; just rethrow so they can suppress navigation
        throw error;
      }
      setError(error.message);
      throw error;
    }
  };

  // ✅ Sign in with email/password - with PWA session
  const emailSignIn = async (email, password) => {
    clearError();
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      if (isPWAMode) {
        storePWASession();
        localStorage.setItem('pwa_user_id', result.user.uid);
        localStorage.setItem('pwa_login_timestamp', Date.now().toString());
        console.log('✅ PWA session created on email login');
      }
      
      return result.user;
    } catch (error) {
      console.error('❌ Error signing in with email:', error);
      setError(error.message);
      throw error;
    }
  };

  // ✅ Sign up with email/password - with PWA session
  const emailSignUp = async (email, password, displayName) => {
    clearError();
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      
      if (isPWAMode) {
        storePWASession();
        localStorage.setItem('pwa_user_id', result.user.uid);
        localStorage.setItem('pwa_login_timestamp', Date.now().toString());
        console.log('✅ PWA session created on signup');
      }
      
      return result.user;
    } catch (error) {
      console.error('❌ Error signing up with email:', error);
      setError(error.message);
      throw error;
    }
  };

  // ✅ UPDATED: Sign out - clear PWA session AND FCM token
  const logout = async () => {
    clearError();
    try {
      // ✅ NEW: Delete FCM token for THIS device only
      if (user?.uid) {
        try {
          await NotificationService.deleteDeviceToken(user.uid);
          console.log('🗑️ FCM device token removed on logout');
        } catch (fcmError) {
          console.warn('⚠️ FCM token deletion failed (non-critical):', fcmError);
          // Don't block logout if FCM deletion fails
        }
      }
      
      // Clear PWA session data
      if (isPWAMode) {
        localStorage.removeItem('pwa_user_id');
        localStorage.removeItem('pwa_login_timestamp');
        localStorage.removeItem('pwa_last_active');
        console.log('🔓 PWA session cleared on logout');
      }

      // Clear cross-subdomain session cookie
      if (window.location.hostname.includes('kalkode.com')) {
        try {
          await fetch('/api/guest-operations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'session_logout' })
          });
        } catch (e) { /* non-critical */ }
      }

      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);

    } catch (error) {
      console.error('❌ Error signing out:', error);
      setError(error.message);
      throw error;
    }
  };

  // ✅ NEW: Logout from all devices - removes ALL FCM tokens
  const logoutAllDevices = async () => {
    clearError();
    try {
      // ✅ Delete ALL FCM tokens for this user
      if (user?.uid) {
        try {
          await NotificationService.deleteAllUserTokens(user.uid);
          console.log('🗑️ All FCM device tokens removed');
        } catch (fcmError) {
          console.warn('⚠️ FCM token deletion failed (non-critical):', fcmError);
        }
      }
      
      // Clear PWA session data
      if (isPWAMode) {
        localStorage.removeItem('pwa_user_id');
        localStorage.removeItem('pwa_login_timestamp');
        localStorage.removeItem('pwa_last_active');
        console.log('🔓 PWA session cleared on logout (all devices)');
      }

      // Clear cross-subdomain session cookie
      if (window.location.hostname.includes('kalkode.com')) {
        try {
          await fetch('/api/guest-operations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'session_logout' })
          });
        } catch (e) { /* non-critical */ }
      }

      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);

    } catch (error) {
      console.error('❌ Error signing out from all devices:', error);
      setError(error.message);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    clearError();
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('❌ Error resetting password:', error);
      setError(error.message);
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    clearError();
    try {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, updates);
        setUser({ ...auth.currentUser });
      }
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      setError(error.message);
      throw error;
    }
  };

  // ✅ Get PWA session info
  const getPWASessionInfo = () => {
    if (!isPWAMode) return null;
    
    const loginTimestamp = localStorage.getItem('pwa_login_timestamp');
    const lastActive = localStorage.getItem('pwa_last_active');
    const userId = localStorage.getItem('pwa_user_id');
    
    if (!loginTimestamp) return null;
    
    const loginDate = new Date(parseInt(loginTimestamp));
    const now = new Date();
    const daysSinceLogin = Math.floor((now - loginDate) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, 7 - daysSinceLogin);
    
    return {
      userId,
      loginDate,
      daysSinceLogin,
      daysRemaining,
      isValid: isPWASessionValid(),
      lastActive: lastActive ? new Date(parseInt(lastActive)) : null
    };
  };

  const value = {
    user,
    loading,
    error,
    clearError,
    signInWithGoogle,
    emailSignIn,
    emailSignUp,
    logout,
    logoutAllDevices, // ✅ NEW - logout from ALL devices
    resetPassword,
    updateUserProfile,
    isAuthenticated,
    isPWAMode,
    getPWASessionInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};